import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as charApi from '../api/characters';
import * as condApi from '../api/conditions';
import * as defApi from '../api/defenses';
import * as customEffApi from '../api/customEffects';
import * as trackerApi from '../api/trackers';
import { api } from '../api/client';
import type {
  CreateCharacterRequest,
  UpdateCharacterRequest,
  SetBoostsRequest,
  UpdateSkillsRequest,
  AddItemRequest,
  UpdateItemRequest,
  AddAfflictionRequest,
  UpdateAfflictionRequest,
  CreateNoteRequest,
  AddSpellRequest,
  UpdateSpellSlotsRequest,
  SetPreparedSlotsRequest,
  AddFeatRequest,
  LevelUpRequest,
  LevelUpPreviewRequest,
  SaveLevelUpPlanRequest,
  AddConditionRequest,
  UpdateConditionRequest,
  AddDefenseRequest,
  UpdateDefenseRequest,
  CharacterWithRelations,
  ImportPathbuilderRequest,
  AddCompanionRequest,
  UpdateCompanionRequest,
  CreateAttackRequest,
  UpdateAttackRequest,
  InventoryItem,
  PrepareStaffRequest,
  AddRitualRequest,
  UpdateRitualRequest,
  ActivateActionRequest,
  DeactivateActionRequest,
  UseActionRequest,
  ResetActionUsesRequest,
  AddCustomEffectRequest,
  UpdateCustomEffectRequest,
  AddTrackerRequest,
  UpdateTrackerRequest,
} from '@charbuilder/shared';
import { queryKeys } from '../lib/query-keys';

export function useCharacters() {
  return useQuery({
    queryKey: queryKeys.characters.all,
    queryFn: charApi.getCharacters,
  });
}

export function useCharacter(id: string) {
  return useQuery({
    queryKey: queryKeys.characters.detail(id),
    queryFn: () => charApi.getCharacter(id),
    enabled: !!id,
  });
}

export function useCreateCharacter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCharacterRequest) => charApi.createCharacter(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.characters.all }),
  });
}

export function useUpdateCharacter(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateCharacterRequest) => charApi.updateCharacter(id, data),
    onSuccess: (data) => {
      qc.setQueryData(
        queryKeys.characters.detail(id),
        (old: CharacterWithRelations | undefined) => old ? { ...old, ...data } : old,
      );
      qc.invalidateQueries({ queryKey: queryKeys.characters.all, exact: true });
    },
  });
}

export function useDeleteCharacter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => charApi.deleteCharacter(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.characters.all }),
  });
}

export function useImportPathbuilder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ImportPathbuilderRequest) => charApi.importFromPathbuilder(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.characters.all }),
  });
}

export function useSetBoosts(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SetBoostsRequest) => charApi.setBoosts(characterId, data),
    onSuccess: (data) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) => old ? { ...old, ...data } : old,
      );
    },
  });
}

export function useUpdateSkills(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateSkillsRequest) => charApi.updateSkills(characterId, data),
    onSuccess: (skills) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, skills } : old,
      );
    },
  });
}

export function useLevelUp(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data?: LevelUpRequest) => charApi.levelUp(characterId, data ?? {}),
    onSuccess: (data) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) => old ? { ...old, ...data } : old,
      );
      qc.invalidateQueries({ queryKey: queryKeys.characters.all, exact: true });
    },
  });
}

export function useLevelUpPreview(characterId: string) {
  return useMutation({
    mutationFn: (data?: LevelUpPreviewRequest) => charApi.previewLevelUp(characterId, data),
  });
}

// ── Level-Up Plan ──

export function useLevelUpPlan(characterId: string) {
  return useQuery({
    queryKey: queryKeys.characters.levelUpPlan(characterId),
    queryFn: () => charApi.getLevelUpPlan(characterId),
    enabled: !!characterId,
  });
}

export function useSaveLevelUpPlan(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SaveLevelUpPlanRequest) => charApi.saveLevelUpPlan(characterId, data),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.characters.levelUpPlan(characterId), data);
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, levelUpPlan: data } : old,
      );
    },
  });
}

export function useDeleteLevelUpPlan(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => charApi.deleteLevelUpPlan(characterId),
    onSuccess: () => {
      qc.setQueryData(queryKeys.characters.levelUpPlan(characterId), null);
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, levelUpPlan: null } : old,
      );
    },
  });
}

// ── Rest ──

export function useTakeRest(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => charApi.takeRest(characterId),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.characters.detail(characterId), data);
    },
  });
}

// ── Battle Form mutations ──

export function useActivateBattleForm(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (battleFormId: string) => charApi.activateBattleForm(characterId, battleFormId),
    onSuccess: (data) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) => old ? { ...old, ...data } : old,
      );
    },
  });
}

export function useDeactivateBattleForm(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => charApi.deactivateBattleForm(characterId),
    onSuccess: (data) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) => old ? { ...old, ...data } : old,
      );
    },
  });
}

// ── Inventory mutations ──

export function useAddItem(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AddItemRequest) =>
      api.post(`/characters/${characterId}/inventory`, data).then((r) => r.data),
    onSuccess: (newItem) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, inventory: [...old.inventory, newItem] } : old,
      );
    },
  });
}

export function useDeleteItem(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      api.delete(`/characters/${characterId}/inventory/${itemId}`),
    onSuccess: (_result, itemId) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, inventory: old.inventory.filter((i) => i.id !== itemId) } : old,
      );
    },
  });
}

export function useUpdateItem(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: UpdateItemRequest }) =>
      api.patch(`/characters/${characterId}/inventory/${itemId}`, data).then((r) => r.data),
    onSuccess: (updated) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, inventory: old.inventory.map((i) => (i.id === updated.id ? updated : i)) } : old,
      );
    },
  });
}

export function useToggleItem(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, field, value }: { itemId: string; field: 'isEquipped' | 'isInvested'; value: boolean }) =>
      api.patch(`/characters/${characterId}/inventory/${itemId}`, { [field]: value }).then((r) => r.data),
    onSuccess: (updated) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, inventory: old.inventory.map((i) => (i.id === updated.id ? updated : i)) } : old,
      );
    },
  });
}

export function useToggleItemIdentified(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => charApi.toggleItemIdentified(characterId, itemId),
    onSuccess: (updated) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, inventory: old.inventory.map((i) => (i.id === updated.id ? updated : i)) } : old,
      );
    },
  });
}

export function useMoveToContainer(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, containerId }: { itemId: string; containerId: string | null }) =>
      charApi.moveToContainer(characterId, itemId, containerId),
    onSuccess: (updated) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, inventory: old.inventory.map((i) => (i.id === updated.id ? updated : i)) } : old,
      );
    },
  });
}

export function useContainerRules() {
  return useQuery({
    queryKey: ['container-rules'],
    queryFn: charApi.getContainerRules,
    staleTime: Infinity,
  });
}

// ── Notes mutations ──

export function useAddNote(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateNoteRequest) =>
      api.post(`/characters/${characterId}/notes`, data).then((r) => r.data),
    onSuccess: (newNote) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, notes: [newNote, ...old.notes] } : old,
      );
    },
  });
}

export function useDeleteNote(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) =>
      api.delete(`/characters/${characterId}/notes/${noteId}`),
    onSuccess: (_result, noteId) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, notes: old.notes.filter((n) => n.id !== noteId) } : old,
      );
    },
  });
}

export function useToggleNotePin(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, isPinned }: { noteId: string; isPinned: boolean }) =>
      api.patch(`/characters/${characterId}/notes/${noteId}`, { isPinned }).then((r) => r.data),
    onSuccess: (updated) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, notes: old.notes.map((n) => (n.id === updated.id ? updated : n)) } : old,
      );
    },
  });
}

// ── Spells mutations ──

export function useAddSpell(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AddSpellRequest) =>
      api.post(`/characters/${characterId}/spells`, data).then((r) => r.data),
    onSuccess: (newSpell) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, spells: [...old.spells, newSpell] } : old,
      );
    },
  });
}

export function useRemoveSpell(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (spellId: string) =>
      api.delete(`/characters/${characterId}/spells/${spellId}`),
    onSuccess: (_result, spellId) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, spells: old.spells.filter((s) => s.id !== spellId) } : old,
      );
    },
  });
}

export function useUpdateSpellSlots(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateSpellSlotsRequest) =>
      api.put(`/characters/${characterId}/spell-slots`, data).then((r) => r.data),
    onSuccess: (slots) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, spellSlots: slots } : old,
      );
    },
  });
}

export function useSetPreparedSlots(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SetPreparedSlotsRequest) => charApi.setPreparedSlots(characterId, data),
    onSuccess: (slots) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, preparedSlots: slots } : old,
      );
    },
  });
}

export function useApplySpellBlendingTrades(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (trades: { fromRank: number; fromCount: number; toRank: number; toCount: number }[]) =>
      charApi.applySpellBlendingTrades(characterId, trades),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.characters.detail(characterId), updated);
    },
  });
}

export function useBuildMakeshiftStaff(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (spells: { name: string; rank: number }[]) => charApi.buildMakeshiftStaff(characterId, spells),
    onSuccess: (updated) => qc.setQueryData(queryKeys.characters.detail(characterId), updated),
  });
}

export function useChargeMakeshiftStaff(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (preparedSlotId: string) => charApi.chargeMakeshiftStaff(characterId, preparedSlotId),
    onSuccess: (updated) => qc.setQueryData(queryKeys.characters.detail(characterId), updated),
  });
}

export function useExpendSlot(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slotId: string) => charApi.expendSlot(characterId, slotId),
    onSuccess: (updated) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old
            ? { ...old, preparedSlots: old.preparedSlots.map((s) => (s.id === updated.id ? updated : s)) }
            : old,
      );
    },
  });
}

export function useResetPreparedSlots(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => charApi.resetPreparedSlots(characterId),
    onSuccess: () => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old
            ? {
                ...old,
                preparedSlots: old.preparedSlots.map((s) => ({ ...s, isExpended: false })),
                spellSlots: old.spellSlots.map((s) => ({ ...s, used: 0 })),
              }
            : old,
      );
    },
  });
}

export function useUpdateSpellRecord(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ spellRecordId, data }: { spellRecordId: string; data: { isSignature?: boolean; isPrepared?: boolean } }) =>
      charApi.updateSpellRecord(characterId, spellRecordId, data),
    onSuccess: (_result, { spellRecordId, data }) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old
            ? { ...old, spells: old.spells.map((s) => (s.id === spellRecordId ? { ...s, ...data } : s)) }
            : old,
      );
    },
  });
}

// ── Feats mutations ──

export function useAddFeat(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AddFeatRequest) => charApi.addFeat(characterId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.characters.detail(characterId) }),
  });
}

export function useRemoveFeat(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (featId: string) => charApi.removeFeat(characterId, featId),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.characters.detail(characterId) }),
  });
}

// ── Conditions mutations ──

export function useAddCondition(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AddConditionRequest) => condApi.addCondition(characterId, data),
    onSuccess: (newCondition) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) => {
          if (!old) return old;
          // Upsert: replace if same condition exists, otherwise append
          const existing = old.conditions.findIndex((c) => c.condition === newCondition.condition);
          const conditions = [...old.conditions];
          if (existing >= 0) {
            conditions[existing] = newCondition;
          } else {
            conditions.push(newCondition);
          }
          return { ...old, conditions };
        },
      );
    },
  });
}

export function useUpdateCondition(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conditionId, data }: { conditionId: string; data: UpdateConditionRequest }) =>
      condApi.updateCondition(characterId, conditionId, data),
    onSuccess: (updated) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old
            ? { ...old, conditions: old.conditions.map((c) => (c.id === updated.id ? updated : c)) }
            : old,
      );
    },
  });
}

export function useDeleteCondition(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conditionId: string) => condApi.deleteCondition(characterId, conditionId),
    onSuccess: (_result, conditionId) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old
            ? { ...old, conditions: old.conditions.filter((c) => c.id !== conditionId) }
            : old,
      );
    },
  });
}

export function useDeleteAllConditions(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => condApi.deleteAllConditions(characterId),
    onSuccess: () => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, conditions: [] } : old,
      );
    },
  });
}

// ── Defenses mutations ──

export function useAddDefense(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AddDefenseRequest) => defApi.addDefense(characterId, data),
    onSuccess: (newDefense) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) => {
          if (!old) return old;
          const currentDefenses = old.defenses ?? [];
          const existing = currentDefenses.findIndex(
            (d) => d.defenseType === newDefense.defenseType && d.damageType === newDefense.damageType,
          );
          const defenses = [...currentDefenses];
          if (existing >= 0) {
            defenses[existing] = newDefense;
          } else {
            defenses.push(newDefense);
          }
          return { ...old, defenses };
        },
      );
    },
  });
}

export function useUpdateDefense(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ defenseId, data }: { defenseId: string; data: UpdateDefenseRequest }) =>
      defApi.updateDefense(characterId, defenseId, data),
    onSuccess: (updated) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old
            ? { ...old, defenses: (old.defenses ?? []).map((d) => (d.id === updated.id ? updated : d)) }
            : old,
      );
    },
  });
}

export function useDeleteDefense(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (defenseId: string) => defApi.deleteDefense(characterId, defenseId),
    onSuccess: (_result, defenseId) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old
            ? { ...old, defenses: (old.defenses ?? []).filter((d) => d.id !== defenseId) }
            : old,
      );
    },
  });
}

// ── Affliction mutations ──

export function useAddAffliction(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AddAfflictionRequest) =>
      api.post(`/characters/${characterId}/afflictions`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.characters.detail(characterId) }),
  });
}

export function useAdvanceAffliction(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (afflictionId: string) =>
      api.post(`/characters/${characterId}/afflictions/${afflictionId}/advance`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.characters.detail(characterId) }),
  });
}

export function useReduceAffliction(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (afflictionId: string) =>
      api.post(`/characters/${characterId}/afflictions/${afflictionId}/reduce`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.characters.detail(characterId) }),
  });
}

export function useUpdateAffliction(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ afflictionId, data }: { afflictionId: string; data: UpdateAfflictionRequest }) =>
      api.patch(`/characters/${characterId}/afflictions/${afflictionId}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.characters.detail(characterId) }),
  });
}

export function useRemoveAffliction(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (afflictionId: string) =>
      api.delete(`/characters/${characterId}/afflictions/${afflictionId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.characters.detail(characterId) }),
  });
}

// ── Companion mutations ──

export function useAddCompanion(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AddCompanionRequest) => charApi.addCompanion(characterId, data),
    onSuccess: (newCompanion) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, companions: [...(old.companions ?? []), newCompanion] } : old,
      );
    },
  });
}

export function useUpdateCompanion(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ companionId, data }: { companionId: string; data: UpdateCompanionRequest }) =>
      charApi.updateCompanion(characterId, companionId, data),
    onSuccess: (updated) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old
            ? { ...old, companions: (old.companions ?? []).map((c) => (c.id === updated.id ? updated : c)) }
            : old,
      );
    },
  });
}

export function useDeleteCompanion(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (companionId: string) => charApi.deleteCompanion(characterId, companionId),
    onSuccess: (_result, companionId) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old
            ? { ...old, companions: (old.companions ?? []).filter((c) => c.id !== companionId) }
            : old,
      );
    },
  });
}

// ── Attack mutations ──

export function useAddAttack(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAttackRequest) =>
      api.post(`/characters/${characterId}/attacks`, data).then((r) => r.data),
    onSuccess: (newAttack) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, attacks: [...(old.attacks ?? []), newAttack] } : old,
      );
    },
  });
}

export function useUpdateAttack(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ attackId, ...data }: UpdateAttackRequest & { attackId: string }) =>
      api.patch(`/characters/${characterId}/attacks/${attackId}`, data).then((r) => r.data),
    onSuccess: (updated) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old
            ? { ...old, attacks: (old.attacks ?? []).map((a) => (a.id === updated.id ? updated : a)) }
            : old,
      );
    },
  });
}

export function useRemoveAttack(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attackId: string) =>
      api.delete(`/characters/${characterId}/attacks/${attackId}`),
    onSuccess: (_result, attackId) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old
            ? { ...old, attacks: (old.attacks ?? []).filter((a) => a.id !== attackId) }
            : old,
      );
    },
  });
}

// ── Item Spell mutations ──

export function useCastItemSpell(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, spellIndex }: { itemId: string; spellIndex: number }) =>
      api.post(`/characters/${characterId}/inventory/${itemId}/cast`, { spellIndex }).then((r) => r.data),
    onSuccess: (result: InventoryItem | { consumed: true; itemId: string }, { itemId }) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) => {
          if (!old) return old;
          if ('consumed' in result && result.consumed) {
            return { ...old, inventory: old.inventory.filter((i) => i.id !== itemId) };
          }
          return {
            ...old,
            inventory: old.inventory.map((i) => (i.id === (result as InventoryItem).id ? result as InventoryItem : i)),
          };
        },
      );
    },
  });
}

export function useOverchargeWand(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, outcome }: { itemId: string; outcome: 'broken' | 'destroyed' }) =>
      api.post(`/characters/${characterId}/inventory/${itemId}/overcharge`, { outcome }).then((r) => r.data),
    onSuccess: (result: { overchargeResult: string; spellCast?: boolean; itemId?: string; item?: InventoryItem }) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) => {
          if (!old) return old;
          if (result.overchargeResult === 'destroyed') {
            return { ...old, inventory: old.inventory.filter((i) => i.id !== result.itemId) };
          }
          if (result.item) {
            return {
              ...old,
              inventory: old.inventory.map((i) => (i.id === result.item!.id ? result.item! : i)),
            };
          }
          return old;
        },
      );
    },
  });
}

// ── Ritual mutations ──

export function useAddRitual(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AddRitualRequest) => charApi.addRitual(characterId, data),
    onSuccess: (newRitual) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, rituals: [...(old.rituals ?? []), newRitual] } : old,
      );
    },
  });
}

export function useUpdateRitual(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ritualId, data }: { ritualId: string; data: UpdateRitualRequest }) =>
      charApi.updateRitual(characterId, ritualId, data),
    onSuccess: (updated) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old
            ? { ...old, rituals: (old.rituals ?? []).map((r) => (r.id === updated.id ? updated : r)) }
            : old,
      );
    },
  });
}

export function useDeleteRitual(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ritualId: string) => charApi.deleteRitual(characterId, ritualId),
    onSuccess: (_result, ritualId) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old
            ? { ...old, rituals: (old.rituals ?? []).filter((r) => r.id !== ritualId) }
            : old,
      );
    },
  });
}

export function usePrepareStaff(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, expendSlotRank }: { itemId: string; expendSlotRank?: number }) =>
      api.post(`/characters/${characterId}/inventory/${itemId}/prepare-staff`, { expendSlotRank } as PrepareStaffRequest).then((r) => r.data),
    onSuccess: (updated: InventoryItem) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old
            ? { ...old, inventory: old.inventory.map((i) => (i.id === updated.id ? updated : i)) }
            : old,
      );
    },
  });
}

// ── Active Actions ──

export function useActivateAction(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ActivateActionRequest) => charApi.activateAction(characterId, data),
    onSuccess: (result) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) => {
          if (!old) return old;
          const actions = [...old.activeActions];
          // Update the activated action
          const idx = actions.findIndex(a => a.actionName === result.activated.actionName);
          if (idx >= 0) actions[idx] = result.activated;
          else actions.push(result.activated);
          // Mark deactivated ones
          for (const name of result.deactivated) {
            const di = actions.findIndex(a => a.actionName === name);
            if (di >= 0) actions[di] = { ...actions[di], isActive: false, activatedAt: null };
          }
          return { ...old, activeActions: actions };
        },
      );
    },
  });
}

export function useDeactivateAction(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: DeactivateActionRequest) => charApi.deactivateAction(characterId, data),
    onSuccess: (result) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) => {
          if (!old) return old;
          return {
            ...old,
            activeActions: old.activeActions.map(a =>
              a.actionName === result.actionName ? result : a,
            ),
          };
        },
      );
    },
  });
}

export function useUseAction(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UseActionRequest) => charApi.useAction(characterId, data),
    onSuccess: (result) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) => {
          if (!old) return old;
          return {
            ...old,
            activeActions: old.activeActions.map(a =>
              a.actionName === result.actionName ? result : a,
            ),
          };
        },
      );
    },
  });
}

export function useResetActionUses(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ResetActionUsesRequest) => charApi.resetActionUses(characterId, data),
    onSuccess: (result) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) => {
          if (!old) return old;
          return {
            ...old,
            activeActions: old.activeActions.map(a =>
              a.actionName === result.actionName ? result : a,
            ),
          };
        },
      );
    },
  });
}

export function useResetAllActionUses(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => charApi.resetAllActionUses(characterId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.characters.detail(characterId) });
    },
  });
}

export function useDeactivateAllActions(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => charApi.deactivateAllActions(characterId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.characters.detail(characterId) });
    },
  });
}

export function useMarkReactionUsed(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (actionName: string) => charApi.markReactionUsed(characterId, actionName),
    onSuccess: (result) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) => {
          if (!old) return old;
          const actions = [...old.activeActions];
          const idx = actions.findIndex(a => a.actionName === result.actionName);
          if (idx >= 0) actions[idx] = result;
          else actions.push(result);
          return { ...old, activeActions: actions };
        },
      );
    },
  });
}

export function useResetReactions(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => charApi.resetReactions(characterId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.characters.detail(characterId) });
    },
  });
}

// ── Custom Effects mutations ──

export function useAddCustomEffect(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AddCustomEffectRequest) => customEffApi.addCustomEffect(characterId, data),
    onSuccess: (newEffect) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, customEffects: [...(old.customEffects ?? []), newEffect] } : old,
      );
    },
  });
}

export function useUpdateCustomEffect(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ effectId, data }: { effectId: string; data: UpdateCustomEffectRequest }) =>
      customEffApi.updateCustomEffect(characterId, effectId, data),
    onSuccess: (updated) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old
            ? { ...old, customEffects: (old.customEffects ?? []).map((e) => (e.id === updated.id ? updated : e)) }
            : old,
      );
    },
  });
}

export function useDeleteCustomEffect(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (effectId: string) => customEffApi.deleteCustomEffect(characterId, effectId),
    onSuccess: (_result, effectId) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old
            ? { ...old, customEffects: (old.customEffects ?? []).filter((e) => e.id !== effectId) }
            : old,
      );
    },
  });
}

/* ── Trackers ── */

export function useAddTracker(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AddTrackerRequest) => trackerApi.addTracker(characterId, data),
    onSuccess: (newTracker) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old ? { ...old, trackers: [...(old.trackers ?? []), newTracker] } : old,
      );
    },
  });
}

export function useUpdateTracker(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ trackerId, data }: { trackerId: string; data: UpdateTrackerRequest }) =>
      trackerApi.updateTracker(characterId, trackerId, data),
    onSuccess: (updated) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old
            ? { ...old, trackers: (old.trackers ?? []).map((t) => (t.id === updated.id ? updated : t)) }
            : old,
      );
    },
  });
}

export function useDeleteTracker(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (trackerId: string) => trackerApi.deleteTracker(characterId, trackerId),
    onSuccess: (_result, trackerId) => {
      qc.setQueryData(
        queryKeys.characters.detail(characterId),
        (old: CharacterWithRelations | undefined) =>
          old
            ? { ...old, trackers: (old.trackers ?? []).filter((t) => t.id !== trackerId) }
            : old,
      );
    },
  });
}

// ── Reflow Elements (Kineticist) ──

export function useReflowImpulse(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (swaps: { oldFeatId: string; newFeatId: string }[]) =>
      api.post(`/characters/${characterId}/reflow-impulse`, { swaps }).then(r => r.data),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.characters.detail(characterId), data);
    },
  });
}
